<template>
  <div class="changes-section">
    <ModalBox
      :visible="showModal"
      :infoTitle="infoTitle"
      :infoMessage="infoMessage"
      @close="showModal = false"
    />

    <p class="section-hint">
      Append-only history of AI pool user create, update, and delete. Date range is required
      (max 31 days). Leave plant IDs empty to include all plants.
    </p>

    <div class="filters">
      <div class="filter-field">
        <label for="changes-start-date">Start date (UTC)</label>
        <input
          id="changes-start-date"
          class="custom-input"
          type="date"
          v-model="startDate"
          :max="endDate"
          required
        />
      </div>
      <div class="filter-field">
        <label for="changes-end-date">End date (UTC)</label>
        <input
          id="changes-end-date"
          class="custom-input"
          type="date"
          v-model="endDate"
          :min="startDate"
          required
        />
      </div>
      <div class="filter-field">
        <label for="changes-plant-ids">Plant IDs</label>
        <input
          id="changes-plant-ids"
          class="custom-input"
          type="text"
          v-model="plantIdsInput"
          placeholder="e.g. 297 or 297, 317"
        />
      </div>
      <div class="filter-field">
        <label for="changes-changed-by">Changed by (user ID)</label>
        <input
          id="changes-changed-by"
          class="custom-input"
          type="text"
          v-model="changedByInput"
          placeholder="Optional"
        />
      </div>
      <div class="filter-field">
        <label for="changes-action">Action</label>
        <select id="changes-action" class="custom-input" v-model="actionFilter">
          <option value="">All</option>
          <option value="set">set</option>
          <option value="update">update</option>
          <option value="delete">delete</option>
        </select>
      </div>
      <div class="filter-field">
        <label for="changes-success">Outcome</label>
        <select id="changes-success" class="custom-input" v-model="successFilter">
          <option value="">All</option>
          <option value="true">Success</option>
          <option value="false">Failure</option>
        </select>
      </div>
      <div class="filter-field">
        <label for="changes-limit">Limit</label>
        <input
          id="changes-limit"
          class="custom-input"
          type="number"
          v-model.number="limit"
          min="1"
          max="500"
        />
      </div>
    </div>

    <button
      class="custom-button search-button"
      :class="{ active: !isLoading }"
      :disabled="isLoading"
      @click="fetchChanges"
    >
      {{ isLoading ? 'Loading...' : 'Search' }}
    </button>

    <div v-if="hasSearched" class="results">
      <h6>Results ({{ changes.length }})</h6>
      <p v-if="changes.length === 0" class="empty-state">No changes in this range.</p>
      <div v-else class="table-wrap">
        <table class="changes-table">
          <thead>
            <tr>
              <th>Time (UTC)</th>
              <th>Plant</th>
              <th>Changed by</th>
              <th>Action</th>
              <th>Success</th>
              <th>Changed fields</th>
              <th>Info</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in changes" :key="row.id">
              <tr class="result-row" @click="toggleRow(row.id)">
                <td>{{ formatDateTime(row.date_added) }}</td>
                <td>{{ row.plant_id }}</td>
                <td>{{ formatActor(row) }}</td>
                <td>{{ row.action }}</td>
                <td>
                  <span :class="row.success ? 'status-ok' : 'status-fail'">
                    {{ row.success ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>{{ formatChangedFields(row.changed_fields) }}</td>
                <td>{{ row.info_text || '—' }}</td>
              </tr>
              <tr v-if="expandedId === row.id" class="detail-row">
                <td colspan="7">
                  <div class="detail-grid">
                    <div>
                      <strong>Before</strong>
                      <pre>{{ formatSnapshot(row.before) }}</pre>
                    </div>
                    <div>
                      <strong>After</strong>
                      <pre>{{ formatSnapshot(row.after) }}</pre>
                    </div>
                  </div>
                  <p v-if="!row.success" class="error-meta">
                    error_code {{ row.error_code }} — {{ row.info_text || '—' }}
                  </p>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '@/services/apiService';
import { getDevModeState } from '@/services/cookieService';
import { eventBus } from '@/services/eventBus';
import ModalBox from '@/components/cards/ModalBox.vue';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const VALID_ACTIONS = ['set', 'update', 'delete'];
const MAX_RANGE_DAYS = 31;
const DEFAULT_LIMIT = 100;
const MAX_LIMIT = 500;

function utcDateString(date) {
  return date.toISOString().slice(0, 10);
}

function defaultDateRange() {
  const now = new Date();
  const endDate = utcDateString(now);
  const start = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - 6,
  ));
  return {
    startDate: utcDateString(start),
    endDate,
  };
}

export default {
  name: 'AiPoolUserChangesSection',
  components: {
    ModalBox,
  },
  data() {
    const range = defaultDateRange();
    return {
      subDataUrl: '/api/v1.1/data',
      startDate: range.startDate,
      endDate: range.endDate,
      plantIdsInput: '',
      changedByInput: '',
      actionFilter: '',
      successFilter: '',
      limit: DEFAULT_LIMIT,
      changes: [],
      expandedId: null,
      hasSearched: false,
      isLoading: false,
      showModal: false,
      infoTitle: '',
      infoMessage: '',
    };
  },
  methods: {
    handleEnvironmentChange(isDevMode) {
      this.subDataUrl = isDevMode ? '/dev-api/api/v1.1/data' : '/api/v1.1/data';
    },
    initializeEnvironment() {
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.subDataUrl = devModeState.isDevMode
          ? '/dev-api/api/v1.1/data'
          : '/api/v1.1/data';
      }
    },
    showError(title, message) {
      this.infoTitle = title;
      this.infoMessage = message;
      this.showModal = true;
    },
    inclusiveDayCount(startDate, endDate) {
      const start = Date.parse(`${startDate}T00:00:00Z`);
      const end = Date.parse(`${endDate}T00:00:00Z`);
      return Math.round((end - start) / 86400000) + 1;
    },
    parsePlantIds() {
      const raw = this.plantIdsInput.trim();
      if (!raw) {
        return { ok: true, ids: [] };
      }
      const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
      const ids = [];
      for (const part of parts) {
        if (!/^\d+$/.test(part) || Number(part) <= 0) {
          return { ok: false, ids: [] };
        }
        ids.push(Number(part));
      }
      return { ok: true, ids };
    },
    parseChangedBy() {
      const raw = this.changedByInput.trim();
      if (!raw) {
        return { ok: true, value: null };
      }
      if (!/^\d+$/.test(raw)) {
        return { ok: false, value: null };
      }
      return { ok: true, value: Number(raw) };
    },
    clampLimit(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return DEFAULT_LIMIT;
      }
      return Math.min(numeric, MAX_LIMIT);
    },
    buildRequestBody() {
      if (!this.startDate || !this.endDate) {
        return { errorCode: 3011, message: 'Missing start_date or end_date (3011).' };
      }
      if (!DATE_PATTERN.test(this.startDate) || !DATE_PATTERN.test(this.endDate)) {
        return { errorCode: 3006, message: 'start_date and end_date must be YYYY-MM-DD (3006).' };
      }
      if (this.startDate > this.endDate) {
        return { errorCode: 3014, message: 'start_date cannot be after end_date (3014).' };
      }
      if (this.inclusiveDayCount(this.startDate, this.endDate) > MAX_RANGE_DAYS) {
        return {
          errorCode: 3014,
          message: `Date range cannot exceed ${MAX_RANGE_DAYS} days (3014).`,
        };
      }

      const plants = this.parsePlantIds();
      if (!plants.ok) {
        return { errorCode: 3006, message: 'Plant IDs must be positive integers (3006).' };
      }

      const changedBy = this.parseChangedBy();
      if (!changedBy.ok) {
        return { errorCode: 3006, message: 'changed_by must be a user ID number (3006).' };
      }

      if (this.actionFilter && !VALID_ACTIONS.includes(this.actionFilter)) {
        return { errorCode: 3014, message: 'action must be set, update, or delete (3014).' };
      }

      const body = {
        start_date: this.startDate,
        end_date: this.endDate,
        limit: this.clampLimit(this.limit),
      };

      if (plants.ids.length === 1) {
        body.plant_id = plants.ids[0];
      } else if (plants.ids.length > 1) {
        body.plant_ids = plants.ids;
      }

      if (changedBy.value !== null) {
        body.changed_by = changedBy.value;
      }
      if (this.actionFilter) {
        body.action = this.actionFilter;
      }
      if (this.successFilter === 'true') {
        body.success = true;
      } else if (this.successFilter === 'false') {
        body.success = false;
      }

      return { body };
    },
    async fetchChanges() {
      const built = this.buildRequestBody();
      if (built.errorCode) {
        this.showError('Invalid request', built.message);
        return;
      }

      this.isLoading = true;
      this.expandedId = null;
      try {
        const url = `${this.subDataUrl}/ai_pool_users/changes/get`;
        const response = await apiRequest('post', url, built.body);

        if (response.errorCode === 0) {
          this.changes = Array.isArray(response.data) ? response.data : [];
          this.hasSearched = true;
          return;
        }

        this.showError(
          'Error',
          response.infoText || `Request failed (${response.errorCode}).`,
        );
      } catch (error) {
        this.showError('Error', 'Unexpected backend response. Please try again later.');
      } finally {
        this.isLoading = false;
      }
    },
    toggleRow(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    formatActor(row) {
      const first = (row.changed_by_first_name || '').trim();
      const last = (row.changed_by_last_name || '').trim();
      const name = `${first} ${last}`.trim();
      if (name) {
        return name;
      }
      return row.changed_by != null ? String(row.changed_by) : '—';
    },
    formatChangedFields(fields) {
      if (!Array.isArray(fields) || fields.length === 0) {
        return '—';
      }
      return fields.join(', ');
    },
    formatSnapshot(value) {
      if (value == null) {
        return '—';
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch (error) {
        return String(value);
      }
    },
    formatDateTime(dateString) {
      if (!dateString) {
        return '—';
      }
      try {
        return new Date(dateString).toISOString().replace('T', ' ').replace('.000Z', 'Z');
      } catch (error) {
        return dateString;
      }
    },
  },
  created() {
    this.initializeEnvironment();
    eventBus.on('environment-changed', this.handleEnvironmentChange);
  },
  beforeUnmount() {
    eventBus.off('environment-changed', this.handleEnvironmentChange);
  },
};
</script>

<style scoped>
.section-hint {
  color: var(--text-tertiary);
  margin-bottom: 16px;
  font-size: 0.85rem;
}

.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-field label {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.custom-input {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-primary);
  padding: 10px 12px;
  width: 100%;
}

.custom-button {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  font-size: 14px;
  padding: 10px 14px;
}

.custom-button.active {
  border-color: #20cc20;
}

.custom-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.search-button {
  margin-bottom: 16px;
}

.results h6 {
  margin: 0 0 10px;
}

.empty-state {
  color: var(--text-tertiary);
  font-size: 0.85rem;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.changes-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
}

.changes-table th,
.changes-table td {
  padding: 10px 12px;
  text-align: left;
  font-size: 0.8rem;
  border-bottom: 1px solid var(--border-color-light);
  color: var(--text-primary);
  vertical-align: top;
}

.changes-table th {
  background: var(--bg-secondary);
  font-weight: 600;
}

.result-row {
  cursor: pointer;
}

.result-row:hover {
  background: var(--bg-secondary);
}

.status-ok {
  color: #20cc20;
  font-weight: 600;
}

.status-fail {
  color: #d0510d;
  font-weight: 600;
}

.detail-row td {
  background: var(--bg-secondary);
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.detail-grid pre {
  margin: 8px 0 0;
  padding: 10px;
  border-radius: 6px;
  background: var(--input-bg);
  border: 1px solid var(--border-color);
  overflow-x: auto;
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.error-meta {
  margin-top: 8px;
  color: #d0510d;
  font-size: 0.8rem;
}

@media (max-width: 750px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
